import pdf from 'pdfjs'
import helvetica from 'pdfjs/font/Helvetica'
import helveticaBold from 'pdfjs/font/Helvetica-Bold'
import format from 'utils/format'
import { timeAsDayjs } from 'utils/time'
import { apiEventResultLines, apiEventTitle, IAntifraudApiUpdate, participantUpdates } from './antifraudApiHistory'
import {
  IAntifraudParticipant,
  IAntifraudProcess,
  IAntifraudReport,
  IAntifraudSavedReview,
  _antifraudParticipantStatus
} from './antifraudAnalysisTypes'

const A4_WIDTH = 210 * pdf.mm
const A4_HEIGHT = 297 * pdf.mm
const PADDING = 32

const BRAND = 0x00441f
const INK = 0x1f2933
const MUTED = 0x6b7280
const RULE = 0xd7dbe0
const ZEBRA = 0xf5f7f8

const STATUS_COLOR = {
  approved: 0x00994f,
  lowRisk: 0xb07d00,
  reproved: 0xc0392b,
  unmapped: 0x5b3a86,
  neutral: MUTED
}

const PARTICIPANT_STATUS: {
  [key in _antifraudParticipantStatus]: { label: string, color: number }
} = {
  approved: { label: 'Aprovado', color: STATUS_COLOR.approved },
  lowRisk: { label: 'Risco baixo', color: STATUS_COLOR.lowRisk },
  reproved: { label: 'Reprovado', color: STATUS_COLOR.reproved },
  partialReproval: { label: 'Reprova parcial', color: STATUS_COLOR.lowRisk },
  retryable: { label: 'Erro na execução', color: STATUS_COLOR.lowRisk },
  needsReview: { label: 'Requer análise manual', color: STATUS_COLOR.unmapped },
  awaitingReturn: { label: 'Sem retorno ainda', color: STATUS_COLOR.neutral },
  waitingClient: { label: 'Aguardando cliente', color: STATUS_COLOR.neutral },
  ocrNotStarted: { label: 'OCR não iniciado', color: STATUS_COLOR.lowRisk },
  communicationFailure: { label: 'Falha de comunicação', color: STATUS_COLOR.reproved }
}

const PROCESS_STATUS: {
  [key in IAntifraudProcess['status']]: { label: string, color: number }
} = {
  approved: { label: 'Aprovado', color: STATUS_COLOR.approved },
  lowRisk: { label: 'Risco baixo', color: STATUS_COLOR.lowRisk },
  reproved: { label: 'Reprovado', color: STATUS_COLOR.reproved },
  retryable: { label: 'Erro na execução', color: STATUS_COLOR.lowRisk },
  unmapped: { label: 'Não mapeado', color: STATUS_COLOR.unmapped },
  waiting: { label: 'Aguardando', color: STATUS_COLOR.neutral },
  notStarted: { label: 'Não iniciado', color: STATUS_COLOR.neutral },
  noAnswer: { label: 'Sem retorno', color: STATUS_COLOR.neutral }
}

const REVIEW_LABEL: { [review: string]: string } = {
  approve: 'Aprovado',
  approveWithRestriction: 'Aprovado com restrição',
  reject: 'Rejeitado'
}

export function processCellText (process: IAntifraudProcess): string {
  const description = (process.description ?? '').trim()

  return description || PROCESS_STATUS[process.status].label
}

export function reportReviews (participant: IAntifraudParticipant): IAntifraudSavedReview[] {
  if (participant.reviewHistory?.length) return participant.reviewHistory

  return participant.savedReview ? [participant.savedReview] : []
}


const STEP_STATUS_LABEL: { [status: string]: string } = {
  starting: 'Não iniciada',
  analysis: 'Em análise',
  approved: 'Aprovada',
  reproved: 'Reprovada',
  remaking: 'Aguardando correção'
}

const dash = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return '-'
  return String(value)
}

// Uma linha em branco entre um bloco e outro, para separar as atualizações e os pareceres
const BLOCK_GAP = 16

const formatDate = (value?: string) => {
  if (!value) return '-'

  const date = timeAsDayjs(value)

  return date.isValid() ? date.format('DD/MM/YYYY [às] HH:mm') : '-'
}

// Linhas de uma atualização no relatório: o que mudou, a consulta anterior e a nova. A fonte padrão
// do PDF não tem seta, então a troca vai escrita
export function updateReportEntries ({ event, previous }: IAntifraudApiUpdate): Array<[string, string]> {
  return [
    ['Atualização', apiEventTitle(event)],
    ['Data', formatDate(event.createdAt)],
    ['Origem', [event.trigger, event.userName].filter(Boolean).join(' · ')],
    ...event.changes.map((change): [string, string] => [
      change.label,
      `de ${change.before || '-'} para ${change.after || '-'}`
    ]),
    ...(previous ? [['Resultado anterior', apiEventResultLines(previous).join('; ')] as [string, string]] : []),
    ['Novo resultado', apiEventResultLines(event).join('; ')],
    ['Data do retorno', formatDate(event.resultAt)]
  ]
}

export async function generateAntifraudReport (data: IAntifraudReport): Promise<string> {
  const document = new pdf.Document({
    font: helvetica,
    fontSize: 10,
    color: INK,
    height: A4_HEIGHT,
    padding: PADDING,
    properties: { title: `Relatorio Antifraude ${data.operation.proposalNumber}` },
    width: A4_WIDTH
  })

  const footer = document.footer()

  footer.text(
    `Relatório gerado em ${timeAsDayjs(new Date()).format('DD/MM/YYYY [às] HH:mm')}`,
    { textAlign: 'left', fontSize: 8, color: MUTED }
  )

  footer.pageNumber(
    (current, total) => `${current} de ${total}`,
    { textAlign: 'right', fontSize: 8, color: MUTED }
  )

  const title = () => {
    document.text('Relatório Antifraude', {
      font: helveticaBold,
      fontSize: 22,
      color: BRAND
    })

    document.text(`Proposta ${dash(data.operation.proposalNumber)}`, {
      fontSize: 11,
      color: MUTED
    })

    document.cell({ borderBottomWidth: 2, borderBottomColor: BRAND, paddingTop: 8, paddingBottom: 0 })
    document.cell({ paddingBottom: 14 })
  }

  const sectionTitle = (text: string) => {
    document.cell({ paddingTop: 6 })
    document.text(text.toUpperCase(), {
      font: helveticaBold,
      fontSize: 9,
      color: BRAND
    })
    document.cell({ borderBottomWidth: 0.5, borderBottomColor: RULE, paddingTop: 3 })
    document.cell({ paddingBottom: 6 })
  }

  const definitionList = (entries: Array<[string, string, number?]>) => {
    const table = document.table({
      widths: [130, null],
      borderVerticalWidth: 0,
      borderHorizontalWidth: 0
    })

    entries.forEach(([label, value, color], index) => {
      const row = table.row()
      const background = index % 2 === 0 ? ZEBRA : 0xffffff

      row.cell(label, {
        font: helveticaBold,
        fontSize: 9,
        color: MUTED,
        backgroundColor: background,
        padding: 6
      })
      row.cell(dash(value), {
        fontSize: 9,
        color: color ?? INK,
        backgroundColor: background,
        padding: 6
      })
    })
  }

  const participantBlock = (participant: IAntifraudParticipant) => {
    const status = PARTICIPANT_STATUS[participant.status]

    document.cell({ paddingTop: 10 })

    const heading = document.cell({
      backgroundColor: BRAND,
      padding: 8
    })
    heading
      .text({ fontSize: 11, color: 0xffffff })
      .append(dash(participant.role).toUpperCase(), { font: helveticaBold })
      .append(`   ${dash(participant.name)}`)

    definitionList([
      ['CPF', dash(participant.person.cpf)],
      ['Endereço', dash(participant.person.address)],
      ['Telefone', dash(participant.person.phone)],
      ['E-mail', dash(participant.person.email)],
      ['Resultado automático', status.label, status.color],
      ['Último retorno', dash(participant.timestamp)]
    ])

    document.cell({ paddingTop: 8 })

    const table = document.table({
      widths: [110, null, 150],
      borderHorizontalWidth: 0.5,
      borderColor: RULE
    })

    const header = table.header({ font: helveticaBold, fontSize: 8, color: 0xffffff })
    header.cell('SOLUÇÃO', { backgroundColor: BRAND, padding: 6 })
    header.cell('TIPO', { backgroundColor: BRAND, padding: 6 })
    header.cell('STATUS', { backgroundColor: BRAND, padding: 6 })

    participant.processes.forEach((process, index) => {
      const row = table.row()
      const background = index % 2 === 0 ? 0xffffff : ZEBRA
      const processStatus = PROCESS_STATUS[process.status]

      row.cell(process.solution, { fontSize: 9, padding: 6, backgroundColor: background })
      row.cell(process.type, { fontSize: 9, padding: 6, backgroundColor: background })
      row.cell(processCellText(process), {
        fontSize: 9,
        color: processStatus.color,
        padding: 6,
        backgroundColor: background
      })
    })

    if (!participant.processes.length) {
      const row = table.row()
      row.cell('Nenhuma validação retornada.', { fontSize: 9, color: MUTED, padding: 6 })
      row.cell('', { padding: 6 })
      row.cell('', { padding: 6 })
    }

    manualReviewBlock(reportReviews(participant))
    updatesBlock(participantUpdates(participant.apiEvents))
  }

  // Consultas depois do primeiro antifraude: sem isso o relatório mostraria o dado novo ao lado do
  // resultado antigo, como se fossem da mesma consulta
  const updatesBlock = (updates: IAntifraudApiUpdate[]) => {
    if (!updates.length) return

    document.cell({ paddingTop: 8 })
    document.text(`ATUALIZAÇÕES (${updates.length})`, { font: helveticaBold, fontSize: 8, color: BRAND })
    document.text('Consultas refeitas depois do primeiro antifraude, por alteração de dados ou reenvio.', {
      fontSize: 8,
      color: MUTED
    })
    document.cell({ paddingBottom: 4 })

    updates.forEach((update, index) => {
      if (index) document.cell({ paddingTop: BLOCK_GAP })

      definitionList(updateReportEntries(update))
    })
  }

  const manualReviewBlock = (reviews: IAntifraudSavedReview[]) => {
    if (!reviews.length) return

    document.cell({ paddingTop: 8 })
    document.text(
      reviews.length > 1 ? `VALIDAÇÃO MANUAL (${reviews.length} pareceres)` : 'VALIDAÇÃO MANUAL',
      { font: helveticaBold, fontSize: 8, color: BRAND }
    )
    document.cell({ paddingBottom: 4 })

    reviews.forEach((review, index) => {
      if (index) document.cell({ paddingTop: BLOCK_GAP })

      definitionList([
        ['Parecer', REVIEW_LABEL[review.review] ?? dash(review.review)],
        ['Login do aprovador', dash(review.userName)],
        ['Data da decisão', formatDate(review.createdAt)],
        ['Comentários', dash(review.comment)],
        ['Evidências', review.evidences.length
          ? review.evidences.map(evidence => evidence.name).join(', ')
          : '-']
      ])
    })
  }

  title()

  sectionTitle('Dados da operação')
  definitionList([
    ['Proposta', dash(data.operation.proposalNumber)],
    ['Produto', format.capitalize(dash(data.operation.product))],
    ['Valor financiado', data.operation.financedValue ? format.formatBRL(data.operation.financedValue) : '-'],
    ['Prazo', data.operation.deadline ? `${data.operation.deadline} meses` : '-']
  ])

  sectionTitle('Situação da etapa')
  definitionList([
    ['Status', dash(data.stepStatus)],
    ['Data da aprovação', formatDate(data.stepApprovedAt)],
    ['Participantes', String(data.participants.length)]
  ])

  sectionTitle('Participantes')
  data.participants.forEach(participantBlock)

  const buffer = await document.asBuffer()
  const blob = new Blob([buffer], { type: 'application/pdf' })

  return URL.createObjectURL(blob)
}

export function buildReportData (
  proposal: {
    number?: number
    _id?: string
    simulation?: { financing?: number, deadline?: number }
    product?: string
  },
  participants: IAntifraudParticipant[],
  step?: { status?: string, approvedAt?: string }
): IAntifraudReport {
  return {
    operation: {
      proposalNumber: String(proposal.number ?? proposal._id ?? '-'),
      product: proposal.product ?? 'Home equity',
      financedValue: proposal.simulation?.financing,
      deadline: proposal.simulation?.deadline
    },
    stepStatus: STEP_STATUS_LABEL[String(step?.status)] ?? 'Em análise',
    stepApprovedAt: step?.approvedAt,
    participants
  }
}
